import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { filterNonExpiryProducts } from '../../utils/formatters';
// Assuming you have a way to restrict access, maybe via routing or context

// Types
type OrganizationOption = {
  id: string;
  name: string;
  wallet_balance: number;
};

type Product = {
  id: string;
  name: string;
  cost: number;
};

const ACTIVATION_COST_PER_SIM = 15;

export function AdjustWallet() {
  const { profile } = useAuth();

  // Form State
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [numSimsFulfilled, setNumSimsFulfilled] = useState<number | string>('');
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [referenceNote, setReferenceNote] = useState<string>('');

  // Data State
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedOrgBalance, setSelectedOrgBalance] = useState<number | null>(null);

  // Control State
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Log when component renders
  console.log("AdjustWallet component rendering..."); 

  // Fetch organizations and products
  const fetchInitialData = useCallback(async () => {
    console.log("AdjustWallet: fetchInitialData started...");
    setLoadingData(true);
    setError(null);
    try {
      console.log("AdjustWallet: Fetching orgs and products...");
      const [orgRes, productRes] = await Promise.all([
        supabase
          .from('organizations')
          .select('id, name, wallet_balance') // Get balance initially too
          .order('name'),
        supabase
          .from('products')
          .select('id, name, cost')
          .eq('is_active', true)
          .order('cost'),
      ]);
      console.log("AdjustWallet: Fetched orgs and products.", { orgRes, productRes });

      if (orgRes.error) throw orgRes.error;
      setOrganizations(orgRes.data || []);

      if (productRes.error) throw productRes.error;
      setProducts(productRes.data || []);
      console.log("AdjustWallet: State updated with fetched data.");

    } catch (err: any) {
      console.error("AdjustWallet: Error fetching admin adjust data:", err);
      setError(err.message || 'Failed to load required data.');
      setOrganizations([]);
      setProducts([]);
    } finally {
      console.log("AdjustWallet: fetchInitialData finished, setting loadingData to false.");
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Update balance display when org changes
  useEffect(() => {
    if (selectedOrgId) {
        const org = organizations.find(o => o.id === selectedOrgId);
        setSelectedOrgBalance(org?.wallet_balance ?? null);
    } else {
        setSelectedOrgBalance(null);
    }
  }, [selectedOrgId, organizations]);

  // --- Calculation Logic ---
  const selectedProduct = useMemo(() => 
    products.find(p => p.id === selectedProductId)
  , [products, selectedProductId]);

  const numericNumSims = typeof numSimsFulfilled === 'number' 
    ? numSimsFulfilled 
    : parseInt(String(numSimsFulfilled), 10);
  const isValidNumSims = !isNaN(numericNumSims) && numericNumSims > 0;

  const activationCost = isValidNumSims ? numericNumSims * ACTIVATION_COST_PER_SIM : 0;
  const initialProductCostTotal = isValidNumSims && selectedProduct ? 
    numericNumSims * selectedProduct.cost : 0;
  const totalDeduction = activationCost + initialProductCostTotal;

  const canAfford = selectedOrgBalance !== null && totalDeduction <= selectedOrgBalance;

  // --- Handlers ---
  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    setSelectedOrgId(event.target.value as string);
    setError(null); // Clear error when changing org
    setSuccessMessage(null);
  };

  const handleNumSimsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
     const value = event.target.value;
    if (value === '' || /^[1-9]\d*$/.test(value)) {
        setNumSimsFulfilled(value === '' ? '' : parseInt(value, 10));
    } else if (value === '0') {
        setNumSimsFulfilled('');
    }
  };

  const handleProductChange = (event: SelectChangeEvent<string>) => {
    setSelectedProductId(event.target.value as string);
  };

  // Placeholder for RPC call
  const deductFundsFromWallet = async (details: any) => {
     console.log("Simulating wallet deduction:", details);
     // const { error } = await supabase.rpc('deduct_wallet_funds', details);
     // if (error) throw error;
     await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
     // Simulate balance update for now - RPC should return new balance ideally
     const newBalance = (selectedOrgBalance ?? 0) - details.totalDeduction;
     setSelectedOrgBalance(newBalance);
     // Also update the main organizations state if possible/needed
     setOrganizations(prev => prev.map(o => o.id === details.organizationId ? { ...o, wallet_balance: newBalance } : o));
     return { success: true };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!selectedOrgId || !isValidNumSims || !selectedProductId || !selectedProduct) {
        setError('Please select an organization, enter a valid number of SIMs fulfilled, and select the initial product used.');
        return;
    }
    
    if (!canAfford) {
        setError('Insufficient wallet balance for this deduction.');
        return;
    }

    if (!profile?.email) {
        setError('Admin user email not found. Cannot process adjustment.');
        return;
    }

    setSubmitting(true);
    try {
        const selectedOrg = organizations.find(o => o.id === selectedOrgId);
        
        const deductionDetails = {
            organizationId: selectedOrgId,
            numSims: numericNumSims,
            activationCost: activationCost,
            productCostTotal: initialProductCostTotal,
            totalDeduction: totalDeduction,
            productId: selectedProductId,
            productName: selectedProduct.name,
            adminUserId: profile.id,
            adminUserEmail: profile.email,
            referenceNote: referenceNote || `Fulfilled request for ${numericNumSims} SIMs.`,
        };

        // Call placeholder RPC function
        const result = await deductFundsFromWallet(deductionDetails);

        if (result.success) {
            const successMsg = `Successfully deducted ${formatCurrency(totalDeduction)} from ${selectedOrg?.name || 'organization'}.`;
            setSuccessMessage(successMsg);
            
            // --- Insert Org-Level Notification --- 
            const notificationMessage = `Wallet adjusted by ${formatCurrency(totalDeduction)} by admin ${profile.email}. Reason: ${deductionDetails.referenceNote}`;
            const { error: notificationError } = await supabase
              .from('notifications')
              .insert({
                  organization_id: selectedOrgId,
                  user_id: null, // Org-level notification
                  type: 'manual_adjustment',
                  message: notificationMessage,
                  related_entity_type: 'organization', // Could also be 'wallet_transaction' if RPC returns ID
                  related_entity_id: selectedOrgId, 
                  is_read: false
              });
              
            if (notificationError) {
                console.error("Failed to create notification for manual adjustment:", notificationError);
                // Log error, maybe add to success message?
                setSuccessMessage(`${successMsg} (Notification failed: ${notificationError.message})`);
            } else {
                 console.log("Manual adjustment notification created successfully.");
            }
            // --- End Insert Notification --- 
            
            // Reset form
            setSelectedOrgId('');
            setNumSimsFulfilled('');
            setSelectedProductId('');
            setReferenceNote('');
            setSelectedOrgBalance(null);
        } else {
             throw new Error('Wallet deduction failed.');
        }

    } catch (err: any) {
        console.error("Error processing deduction:", err);
        setError(err.message || 'Failed to process deduction.');
    } finally {
        setSubmitting(false);
    }
  };
  
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'R-.--';
    return `R${amount.toFixed(2)}`;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Admin: Adjust Wallet Balance (Fulfillment)
      </Typography>

      {loadingData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ p: 3 }}>
           <Typography variant="body2" color="text.secondary" gutterBottom>
             Use this form after manually fulfilling a SIM request (assigning via bulk upload and performing initial top-up) to deduct the corresponding activation and initial product costs from the organization's wallet.
           </Typography>
           <Divider sx={{ my: 2 }} />

          <Box component="form" onSubmit={handleSubmit} noValidate>
             {error && !submitting && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
             )}
             {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
             )}
             
            <Grid container spacing={3}>
              {/* Column 1: Selection */} 
              <Grid size={{ xs: 12, md: 6 }}>
                 <FormControl fullWidth margin="normal" required>
                    <InputLabel id="org-select-label">Organization</InputLabel>
                    <Select
                      labelId="org-select-label"
                      value={selectedOrgId}
                      label="Organization"
                      onChange={handleOrgChange}
                      disabled={organizations.length === 0}
                    >
                      <MenuItem value="" disabled><em>Select Organization...</em></MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    required
                    fullWidth
                    margin="normal"
                    id="numSims"
                    name="numSims"
                    label="Number of SIMs Fulfilled"
                    type="number" 
                    inputProps={{ min: 1, step: 1 }}
                    value={numSimsFulfilled}
                    onChange={handleNumSimsChange}
                    error={numSimsFulfilled !== '' && !isValidNumSims}
                    helperText={numSimsFulfilled !== '' && !isValidNumSims ? "Enter valid number (1+)" : ""}
                  />

                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="product-select-label">Initial Product Applied</InputLabel>
                    <Select
                      labelId="product-select-label"
                      value={selectedProductId}
                      label="Initial Product Applied"
                      onChange={handleProductChange}
                      disabled={products.length === 0}
                    >
                      <MenuItem value="" disabled><em>Select Product...</em></MenuItem>
                      {filterNonExpiryProducts(products).map((product) => (
                        <MenuItem key={product.id} value={product.id}>
                           {`${product.name} (${formatCurrency(product.cost)})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                   <TextField
                    fullWidth
                    margin="normal"
                    id="referenceNote"
                    name="referenceNote"
                    label="Reference Note (Optional)"
                    value={referenceNote}
                    onChange={(e) => setReferenceNote(e.target.value)}
                  />
              </Grid>

              {/* Column 2: Summary & Action */} 
              <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1, mt: {xs: 0, md: 4} }}>
                     <Typography variant="body2">Organization Balance:</Typography>
                     <Typography variant="h6" gutterBottom>{formatCurrency(selectedOrgBalance)}</Typography>
                     <Divider sx={{ mb: 1 }} />

                     <Typography variant="body2">Activation Cost:</Typography>
                     <Typography variant="h6" gutterBottom>{formatCurrency(activationCost)}</Typography>

                     <Typography variant="body2">Initial Product Cost:</Typography>
                     <Typography variant="h6" gutterBottom>{formatCurrency(initialProductCostTotal)}</Typography>
                     
                     <Divider sx={{ my: 1 }} />

                     <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Deduction:</Typography>
                     <Typography variant="h5" color="error" gutterBottom sx={{ color: 'red' }}>{formatCurrency(totalDeduction)}</Typography>

                     {selectedOrgId && !canAfford && totalDeduction > 0 && (
                         <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                          Insufficient balance to perform this deduction.
                        </Alert>
                     )} 
                  </Box>

                 <Button 
                    fullWidth
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={submitting || loadingData || !selectedOrgId || !isValidNumSims || !selectedProductId || !canAfford}
                    sx={{ mt: 3, py: 1.5 }}
                >
                  {submitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm Deduction'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      )}
    </Container>
  );
} 